/**
*
* VersionsList
*
*/

import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { FormattedMessage } from 'react-intl';
import { isEqual } from 'lodash';

import {
  List,
  ListBody,
  ListItem,
  ListItemColumn,
} from '@pearsontechnology/aero-components';

import {
  commandAuthorPluginVersionDelete,
  commandAuthorPluginVersionUnpublish,
  commandWorkspacePluginDetails,
  commandWorkspacePluginVersionList,
} from 'containers/ConnectionManager/actions';
import { selectPluginDetails, selectPluginVersionList } from 'Workspace/Plugins/store/selectors';
import pluginDetailsShape from 'Workspace/types/pluginDetails';
import VersionListItemActions from 'Workspace/Plugins/components/PluginVersionListPage/VersionListItemActions';
import { selectCurrentMe } from 'data/CurrentUser/selectors';
import { ActionTypes, PermissionTypes } from 'constants/enums';

import PlaceholderList from './PlaceholderList';
import messages from './messages';
import {
  Label,
  MajorUpdateLabel,
  ListItemColumnRightAlign,
  EmptyAction,
} from './styles';

TimeAgo.locale(en);

const addMajorToVersions = (versions) => {
  const taggedVersions = [...versions];

  let currentMajor;
  for (let i = 0; i < taggedVersions.length; i += 1) {
    const major = parseInt(taggedVersions[i].version.split('.')[0], 10);
    if (currentMajor !== major) {
      currentMajor = major;
      taggedVersions[i].major = true;
    }
  }

  return taggedVersions;
};

const parseMajorMinorPatch = (pluginVersionExpr) => {
  const major = parseInt(pluginVersionExpr.split('.')[0], 0);
  const minor = parseInt(pluginVersionExpr.split('.')[1], 0);
  const patch = parseInt(pluginVersionExpr.split('.')[2], 0);
  return { major, minor, patch };
};

const timeAgo = new TimeAgo('en-US');
const getElapsedTime = (date) => timeAgo.format(new Date(date));

class VersionsList extends React.Component {
  state = { isCurrentUserOwner: false }

  componentDidMount() {
    const { pluginId, getPluginVersionList, getPluginDetails, currentUser, pluginDetails } = this.props;
    getPluginVersionList(pluginId);
    getPluginDetails(pluginId);
    this.setCurrentUserOwnerFlag(currentUser, pluginDetails);
  }

  componentDidUpdate(prevProps) {
    const { pluginDetails: prevPluginDetails } = prevProps;
    const { currentUser, pluginDetails } = this.props;
    if (!isEqual(prevPluginDetails, pluginDetails)) {
      this.setCurrentUserOwnerFlag(currentUser, pluginDetails);
    }
  }

  setCurrentUserOwnerFlag = (currentUser, pluginDetails) => {
    let isCurrentUserOwner = false;
    pluginDetails &&
      pluginDetails.collaborators?.accounts.map((collaboratorAccount) => {
        if (
          currentUser.accountId === pluginDetails.summary?.creator?.accountId ||
          (collaboratorAccount.permissionLevel === PermissionTypes.OWNER &&
            currentUser.accountId === collaboratorAccount.account.accountId)
        ) {
          isCurrentUserOwner = true;
        }
      });
    this.setState({ isCurrentUserOwner });
  }

  onAction = (type, pluginId, pluginVersionExpr) => {
    const { deletePluginVersion, unpublishPluginVersion } = this.props;
    const { major, minor, patch } = parseMajorMinorPatch(pluginVersionExpr);

    switch (type) {
      case ActionTypes.DELETE:
        return deletePluginVersion(pluginId, pluginVersionExpr);
      case ActionTypes.UNPUBLISH:
        return unpublishPluginVersion(pluginId, major, minor, patch);
      default:
        return null;
    }
  }

  render() {
    const { pluginVersions, pluginDetails } = this.props;
    const { isCurrentUserOwner } = this.state;

    if (typeof pluginVersions === 'undefined' || pluginVersions.length === 0) {
      return <PlaceholderList />;
    }

    return (
      <List>
        <ListBody>
          {pluginVersions && addMajorToVersions(pluginVersions).map(
            ({ major, version, unpublished, releaseDate }) => (
              <ListItem key={version} compact>
                <ListItemColumn colWidth="45%">
                  {major
                    ? (
                      <MajorUpdateLabel>
                        <Label major unpublished={unpublished}>{version}</Label>
                        <FormattedMessage {...messages.major} />
                      </MajorUpdateLabel>)
                    : <Label unpublished={unpublished}>{version}</Label>
                  }
                </ListItemColumn>
                <ListItemColumnRightAlign>
                  <span title={releaseDate}>
                    {getElapsedTime(releaseDate)}
                  </span>
                </ListItemColumnRightAlign>
                <ListItemColumn colWidth="34px" colWidthMobile="34px" grow="0">
                  {isCurrentUserOwner
                    ? (
                      <VersionListItemActions
                        onAction={this.onAction}
                        version={version}
                        unpublished={unpublished}
                        summary={pluginDetails?.summary}
                      />)
                    : <EmptyAction />
                  }
                </ListItemColumn>
              </ListItem>
            ))}
        </ListBody>
      </List>
    );
  }
}

VersionsList.propTypes = {
  pluginDetails: pluginDetailsShape,
  pluginId: PropTypes.string,
  pluginVersions: PropTypes.array,
  getPluginVersionList: PropTypes.func,
  getPluginDetails: PropTypes.func,
  currentUser: PropTypes.shape({
    accountId: PropTypes.string.isRequired,
  }),
  deletePluginVersion: PropTypes.func,
  unpublishPluginVersion: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => ({
  getPluginDetails: (pluginId) => dispatch(commandWorkspacePluginDetails(pluginId)),
  getPluginVersionList: (pluginId) => dispatch(commandWorkspacePluginVersionList(pluginId)),
  deletePluginVersion: (pluginId, version) => dispatch(commandAuthorPluginVersionDelete(pluginId, version)),
  unpublishPluginVersion: (pluginId, major, minor, patch) => dispatch(commandAuthorPluginVersionUnpublish(pluginId, major, minor, patch)),
});

const mapStateToProps = (state, ownProps) => ({
  pluginDetails: selectPluginDetails(state, ownProps.pluginId),
  pluginVersions: selectPluginVersionList(state, ownProps.pluginId),
  currentUser: selectCurrentMe(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(VersionsList);
